import { TranslatedEnvironmentDTO } from "../../dto/TranslatedEnvironmentDTO";
import { CSSProperties } from "react";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import CheckIcon from "@mui/icons-material/Check";

export default function TranslatedEnvironmentCard(props: {
  environment: TranslatedEnvironmentDTO;
  style: CSSProperties;
  selected: boolean;
  onSelectEnvironment: (environment: TranslatedEnvironmentDTO) => void;
}) {
  const { environment, style, selected, onSelectEnvironment } = { ...props };
  // Placeholder rows reserved by the slice until their page has been fetched.
  const isLoading = environment?.id === "loading";

  const select = () => {
    if (!isLoading) onSelectEnvironment(environment);
  };

  return (
    <div
      className={"box-border p-[8px]"}
      style={style}
      data-testid={`translated-environment-card-${environment?.id}`}
    >
      <div
        role="radio"
        aria-checked={selected}
        aria-disabled={isLoading}
        aria-label={environment?.name}
        tabIndex={isLoading ? -1 : 0}
        onClick={select}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            select();
          }
        }}
        className={
          "relative w-full h-full flex flex-col overflow-hidden rounded-[8px] border-[2px] border-solid bg-creator-surface outline-none transition-colors " +
          (selected
            ? "border-creator-primary"
            : "border-creator-border") +
          (isLoading
            ? ""
            : " cursor-pointer hover:border-creator-primary-hover focus-visible:border-creator-primary-hover")
        }
      >
        {isLoading && (
          <div className={"w-full flex-1 min-h-0 bg-creator-surfaceHover animate-pulse"} />
        )}
        {!isLoading && environment?.thumbnailUri && (
          <img
            src={environment.thumbnailUri}
            alt=""
            draggable={false}
            className={"w-full flex-1 min-h-0 object-cover"}
          />
        )}
        {!isLoading && !environment?.thumbnailUri && (
          <div
            className={
              "w-full flex-1 min-h-0 bg-interaction-softBackground flex flex-col items-center justify-center gap-[4px] text-[36px] text-interaction-disabled"
            }
          >
            <ImageNotSupportedOutlinedIcon fontSize={"inherit"} />
            <span className={"text-[12px] italic"}>no image</span>
          </div>
        )}
        {selected && (
          <span
            className={
              "absolute top-[8px] right-[8px] w-[22px] h-[22px] rounded-full bg-creator-primary text-white flex items-center justify-center"
            }
            data-testid={`translated-environment-card-check-${environment?.id}`}
          >
            <CheckIcon sx={{ fontSize: 16 }} />
          </span>
        )}
        <div className={"shrink-0 px-[12px] py-[8px] flex flex-col gap-y-[2px]"}>
          <div
            className={
              "font-semibold text-[14px] truncate " +
              (selected ? "text-creator-primary" : "text-creator-text")
            }
            data-testid={`translated-environment-card-name-${environment?.id}`}
          >
            {environment?.name}
          </div>
          <div
            className={"text-[12px] text-creator-text-sub truncate min-h-[18px]"}
            title={environment?.description}
          >
            {environment?.description}
          </div>
        </div>
      </div>
    </div>
  );
}
